import { FC, useState } from 'react';
import { Button, chakra, Icon } from '@chakra-ui/react';

import { useDocumentTransitions } from '~/core/shared/api/documents/hooks';
import { useWorkflow } from '~/core/shared/api/workflows/hooks';
import {
  useActiveWorkflowOrFail,
  useRootDocumentOrFail,
  useSwitchToWorkflow,
} from '~/core/shared/hooks';
import { useToast } from '~/core/shared/lib/chakraUi/hooks';
import { WorkflowTileBlock } from '~/core/shared/types';
import { useSubscribeToTransitionPerformed } from '~/core/shared/websocketApi/document';
import { useIsMobileScreenWidth } from '../../shared/hooks';
import { usePerformTransitionAndNavigate } from '../hooks/usePerformTransitionAndNavigate';
import { AddAdditionalContentButton, NavButton } from './buttons';

const renderTileVariant = (
  { loading, title, variant }: WorkflowTileBlock,
  isMobileScreenWidth: boolean,
  onClick: () => void,
) => {
  switch (variant.type) {
    case 'button':
      if (isMobileScreenWidth && variant.isResponsive) {
        return (
          <chakra.button
            alignItems="center"
            colorPalette="brandSecondary"
            display="flex"
            onClick={onClick}
            type="button"
          >
            <Icon as={variant.icon} boxSize={6} />
          </chakra.button>
        );
      }
      return (
        <Button
          colorPalette="brandSecondary"
          loading={loading}
          onClick={onClick}
          type="button"
          variant="outline"
        >
          {variant.icon && <Icon as={variant.icon} />}
          {title}
        </Button>
      );
    case 'add-button':
      return (
        <AddAdditionalContentButton
          loading={loading}
          onClick={onClick}
          w="100%"
        >
          {title}
        </AddAdditionalContentButton>
      );
    case 'nav-button':
    default:
      return (
        <NavButton loading={loading} onClick={onClick} w="100%">
          {title}
        </NavButton>
      );
  }
};

export const TileBlockRenderer: FC<WorkflowTileBlock> = (props) => {
  const isMobileScreenWidth = useIsMobileScreenWidth();
  const switchToWorkflow = useSwitchToWorkflow();
  const rootDocument = useRootDocumentOrFail();
  const { workflow: activeWorkflowName } = useActiveWorkflowOrFail();
  const { behaviour } = props;
  const toast = useToast();
  const [loading, setLoading] = useState<boolean>(false);

  const performTransition = usePerformTransitionAndNavigate(
    behaviour.type === 'transition' ? behaviour.transition : undefined,
  );

  /**
   * Visibility is server-driven and query-backed, so tiles that check the same
   * target share a single request via the query cache instead of racing (and
   * cancelling) duplicate in-flight requests.
   */

  // A workflow tile is visible when the target workflow can be retrieved
  // (after backend auth and guard checks).
  let targetWorkflowName: string | undefined;
  switch (behaviour.type) {
    case 'switch-workflow':
      targetWorkflowName = behaviour.workflow;
      break;
    case 'current-workflow':
      targetWorkflowName = rootDocument.currentWorkflow;
      break;
    default:
      targetWorkflowName = undefined;
  }

  const { workflow: targetWorkflow } = useWorkflow(
    { definition: rootDocument.definition, workflow: targetWorkflowName },
    rootDocument.id,
    targetWorkflowName,
  );

  // A transition tile is visible while its transition is enabled for the
  // document in the active workflow.
  const { transitions, refetch: refetchTransitions } = useDocumentTransitions(
    { definition: rootDocument.definition, workflow: activeWorkflowName },
    rootDocument.id,
    behaviour.type === 'transition',
  );

  useSubscribeToTransitionPerformed(rootDocument.id, () => {
    if (behaviour.type === 'transition') {
      refetchTransitions();
    }
  });

  const resolveTileBehaviour = async () => {
    setLoading(true);

    const switchWorkflow = async (workflow: string) => {
      const success = await switchToWorkflow(workflow);
      if (!success) {
        toast({
          description: 'Could not connect.',
          type: 'error',
          title: 'Error!',
        });
      }
    };

    switch (behaviour.type) {
      case 'transition':
        await performTransition();
        break;
      case 'switch-workflow':
        await switchWorkflow(behaviour.workflow);
        break;
      case 'current-workflow':
        await switchWorkflow(rootDocument.currentWorkflow);
        break;
      default:
    }

    setLoading(false);
  };

  let visible: boolean;
  switch (behaviour.type) {
    case 'transition':
      visible = Boolean(
        behaviour.transition &&
          transitions?.enabledTransitions.includes(behaviour.transition),
      );
      break;
    case 'switch-workflow':
    case 'current-workflow':
      visible = Boolean(targetWorkflow);
      break;
    default:
      visible = true;
  }

  if (!visible) {
    return null;
  }

  return renderTileVariant(
    { loading, ...props },
    isMobileScreenWidth,
    resolveTileBehaviour,
  );
};
